<script setup lang="ts">
import { useRouter } from 'vue-router'

interface Props {
  headline?: string
  subheadline?: string
  primaryBtnText?: string
  primaryBtnLink?: string
  secondaryBtnText?: string
  secondaryBtnLink?: string
  trustText?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  headline: '工作做一遍，台账自动生成',
  subheadline: '让过程本身就是台账。',
  primaryBtnText: '免费试用',
  primaryBtnLink: '/login',
  secondaryBtnText: '查看场景',
  secondaryBtnLink: '#scenarios',
  trustText: () => ['无需信用卡', '3 分钟上手', '免费体验全部功能'],
})

const router = useRouter()

function handleTryClick() {
  router.push(props.primaryBtnLink)
}

function handleDemoClick() {
  const el = document.getElementById('scenarios')
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}
</script>

<template>
  <section
    id="hero"
    class="hero-section relative flex min-h-[700px] items-center justify-center overflow-hidden pt-16"
  >
    <!-- 多层渐变背景 -->
    <div class="pointer-events-none absolute inset-0">
      <div class="absolute inset-0 bg-gradient-to-b from-[#0F172A] via-[#1E293B] to-[#3B5BDB]" />
      <!-- 装饰光晕 -->
      <div class="absolute top-1/4 left-1/4 h-[500px] w-[500px] rounded-full bg-[#3B5BDB]/20 blur-[120px]" />
      <div class="absolute bottom-1/4 right-1/4 h-[400px] w-[400px] rounded-full bg-[#7048E8]/15 blur-[100px]" />
      <div class="absolute top-1/2 left-1/2 h-[300px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#5C7CFA]/10 blur-[80px]" />
      <!-- 网格 -->
      <div class="absolute inset-0 opacity-[0.04]" style="background-image: linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px); background-size: 80px 80px;" />
    </div>

    <div class="relative z-10 mx-auto max-w-[860px] px-6 py-24 text-center">
      <!-- 产品标识 -->
      <div class="mb-8 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm px-5 py-2.5 text-sm font-medium text-white/80 animate-fade-in-up">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-[#5C7CFA] to-[#9775FA] text-xs font-bold text-white shadow-sm">智</span>
        基层工作全过程数字化记录与管理平台
      </div>

      <!-- 核心价值主张 -->
      <h1 class="hero-headline mb-5 text-4xl font-extrabold text-white md:text-5xl lg:text-[3.5rem] leading-tight tracking-tight animate-fade-in-up" style="animation-delay: 0.1s;">
        {{ headline }}
      </h1>

      <!-- 副标题 -->
      <p class="mx-auto mb-12 max-w-[540px] text-lg text-white/60 md:text-xl leading-relaxed animate-fade-in-up" style="animation-delay: 0.2s;">
        {{ subheadline }}
      </p>

      <!-- CTA 按钮 -->
      <div class="flex flex-col items-center justify-center gap-4 sm:flex-row animate-fade-in-up" style="animation-delay: 0.3s;">
        <button
          class="hero-btn-primary w-full rounded-full px-10 py-4 text-base font-semibold text-white shadow-lg shadow-[#3B5BDB]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#3B5BDB]/40 hover:-translate-y-0.5 sm:w-auto"
          @click="handleTryClick"
        >
          {{ primaryBtnText }}
        </button>
        <button
          class="w-full rounded-full border border-white/20 bg-white/5 backdrop-blur-sm px-10 py-4 text-base font-semibold text-white/80 transition-all duration-300 hover:bg-white/10 hover:border-white/30 sm:w-auto"
          @click="handleDemoClick"
        >
          {{ secondaryBtnText }}
        </button>
      </div>

      <!-- 信任提示 -->
      <div class="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-white/40 animate-fade-in-up" style="animation-delay: 0.4s;">
        <span v-for="(text, idx) in trustText" :key="idx" class="flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5C7CFA" stroke-width="2.5" stroke-linecap="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          {{ text }}
        </span>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hero-btn-primary {
  background: linear-gradient(135deg, #3B5BDB 0%, #7048E8 100%);
}
</style>
